<template>
  <div>
    <v-data-table
      :style="isFlat(attributesTable.flat)"
      :items-per-page="options.itemsPerPage"
      v-bind="attributesTable.dataTable"
      :item-key="attributesTable.keyValue"
      :expanded.sync="expanded"
      :options.sync="options"
      :headers="!!expandHeaders.length ? headerComputed : headers"
      :items="items"
      :search="search"
      :loading="loading"
      :server-items-length="serverItemsLength"
      hide-default-footer
      v-model="selected"
      ref="refreshTable"
      @item-selected="selectItem"
      @toggle-select-all="selectAllItem"
      @current-items="currentItems = $event"
      @page-count="pageCount = $event"
      @item-expanded="loadExpandDetails($event)"
      :class="[
        'v-data-table__custom elevation-2',
        attributesTable.dataTable.expandAll && 'v-data-table__custom-header-editable',
        attributesTable.dataTable.keepRowVisibleOnExpand && 'v-data-table__expanded__row__visible'
      ]"
    >
      <div
        v-for="(header) in headerComputed" 
        :key="header.value" 
        :slot="`item.${header.value}`" 
        slot-scope="{item}"
        :style="getStyle(item[header.value], header.value, item)"
      >
        {{ item[header.value] }}
      </div>

      <template v-slot:top v-if="attributesTable.showSearch">
        <v-card-title class="pt-1 elevation-0">
          <span v-if="attributesTable.titleTable">{{ attributesTable.titleTable }}</span>
          <v-spacer></v-spacer>
          <v-text-field
            v-model="search"
            append-icon="search"
            label="Pesquisar"
            single-line
            hide-details
            id="input-search"
          ></v-text-field>
        </v-card-title>
      </template>

      <template v-if="loading" v-slot:progress>
        <v-progress-linear color="error" :height="6" indeterminate></v-progress-linear>
      </template>

      <template v-slot:[`item.action`]="{ item }">
        <v-layout>
          <v-tooltip bottom :color="attributesTable?.attributesEditIcon?.color ?? 'primary'" :disabled="!attributesTable.editIconTooltip">
            <template v-slot:activator="{ on, attrs }">
              <v-icon
                :data-has-tooltip="attributesTable.editIconTooltip ? 'true' : 'false'"
                :data-tooltip="attributesTable.editIconTooltip"
                v-on="on"
                small
                :color="attributesTable?.attributesEditIcon?.color ?? 'primary'"
                @click="edit(item)"
                :id="`edit-table-${item[attributesTable.keyValue]}`"
                :name="`edit-table-${item[attributesTable.keyValue]}`"
                v-bind="[attrs, attributesTable?.attributesEditIcon]"
                >{{ attributesTable.editIcon }}</v-icon
              >
            </template>
            <span>{{ attributesTable.editIconTooltip }}</span>
          </v-tooltip>

          <i4proRecordActionItem v-if="groupActions()"
            :item="item"
            :lineActions="lineActions(item)"
            :keyValue="attributesTable.keyValue"
            @handleExecuteAction="handleExecuteAction($event)"
          />
        </v-layout>
      </template>

      <template 
        v-for="slot in slotAction" 
        v-slot:[`item.${slot}`]="{ item }"
      >
        <v-layout class="justify-between gap-2">
          <span>{{ item[slot] }}</span>
          <template v-for="(action, key) in lineActionsSlot(item, slot)">
            <v-tooltip bottom :color="action.tooltipColorClass" :key="key" :disabled="!action.tooltip">
              <template v-slot:activator="{ on, attrs }">
                <v-icon
                  :data-has-tooltip="action.tooltip ? 'true' : 'false'"
                  :data-tooltip="action.tooltip"
                  small
                  v-on="on"
                  v-bind="[action.attributesIcon, attrs]"
                  :key="key"
                  class="mr-1"
                  @click="executeAction(action, 'list', item)"
                  :id="`${action.id}-${item[attributesTable.keyValue]}`"
                  :name="`${action.name}-${item[attributesTable.keyValue]}`"
                  >{{ action.attributesIcon.icon }}</v-icon
                >
              </template>
              <span>{{ action.tooltip }}</span>
            </v-tooltip>
          </template>
        </v-layout>
      </template>

      <template v-slot:[`item.recordAction`]="{ item }">
        <v-layout>
          <i4proRecordActionItem
            :item="item"
            :lineActions="lineActions(item)"
            :keyValue="attributesTable.keyValue"
            @handleExecuteAction="handleExecuteAction($event)"
          />
        </v-layout>
      </template>

      <template v-slot:item.data-table-expand="{ item, isExpanded, expand }">
        <v-btn
          icon
          @click="expand(!isExpanded)"
          class="v-data-table__expand-icon"
        >
          <v-icon v-if="!!attributesTable.dataTable.customExpandIcon"
            :color="attributesTable.dataTable.customExpandIcon.attributes?.color || 'primary'"
            :size="attributesTable.dataTable.customExpandIcon.attributes?.size || '24'"
            :class="attributesTable.dataTable.customExpandIcon.attributes?.class || ''"
          >
            {{ isExpanded ? attributesTable.dataTable.customExpandIcon.close || 'mdi-chevron-up' : attributesTable.dataTable.customExpandIcon.open || 'mdi-chevron-down' }}
          </v-icon>

          <v-icon v-else>
            {{ isExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
          </v-icon>
        </v-btn>
      </template>

      <template v-slot:expanded-item="{ headers, item }">
        <td :colspan="headers.length">
          <v-icon
            v-if="!attributesTable.dataTable.expandAll && !attributesTable.dataTable.keepRowVisibleOnExpand"
            class="v-icon--close"
            small
            @click="handleCloseExpandedItem(item)"
            >close</v-icon
          >

          <v-layout v-if="schemaExpandDetails" row class="mb-0 mt-0">
            <v-col>
              <component v-if="renderExpandDetails" :is="expandDetailsComponent" :page="expandDetailsPage" :context="expandDetailsContext" :origin="'page'" />
            </v-col>
          </v-layout>

          <v-layout v-else-if="!!expandHeaders.length" row class="mb-0 mt-0">
            <v-col>
              <v-data-table v-if="!!expanded.length"
                :items="expandItems(item)"
                :headers="expandHeaders"
                :item-key="attributesTable.keyValue"
                hide-default-footer
                class="v-data-table__custom elevation-2"
              >
                <template v-slot:[`item.recordAction`]="{ item }">
                  <v-layout>
                    <i4proRecordActionItem v-if="groupActions()"
                      :item="item"
                      :lineActions="expandLineActions(item)"
                      :keyValue="attributesTable.keyValue"
                      @handleExecuteAction="handleExecuteAction($event)"
                    />
                  </v-layout>
                </template>
              </v-data-table>
            </v-col>
          </v-layout>

          <v-layout v-else row class="mb-0 mt-0">
            <v-col sm="11" cols="12" class="p-0">
              <v-form :ref="`form-${item[attributesTable.keyValue]}`">
                <vue-form-generator
                  tag="div"
                  v-if="schemaFields"
                  :schema="{ fields: schemaFieldsSelected(item) }"
                  :options="{ fieldIdPrefix: getFieldIdPrefix(item), mode: 'list', page, item, index: getIndex(item), items: currentItems }"
                  :model="getItemModify(item)"
                />
              </v-form>
            </v-col>
            <v-col sm="1" cols="12" class="action-col p-0">
              <template v-for="(action, key) in conditioningActions(item)">
                <v-tooltip bottom :key="key" :color="action.tooltipColorClass" :disabled="!action.tooltip">
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon
                      :data-has-tooltip="action.tooltip ? 'true' : 'false'"
                      :data-tooltip="action.tooltip"
                      v-on="on"
                      small
                      v-bind="[action.attributesIcon, attrs]"
                      :key="key"
                      @click="executeAction(action, 'list', item)"
                      :id="`${action.id}-${item[attributesTable.keyValue]}`"
                      :name="`${action.name}-${item[attributesTable.keyValue]}`"
                      >{{ action.attributesIcon.icon }}</v-icon
                    >
                  </template>
                  <span>{{ action.tooltip }}</span>
                </v-tooltip>
              </template>
            </v-col>
          </v-layout>
        </td>
      </template>

      <template v-if="showTableFooter" v-slot:footer="{ props }">
        <v-data-footer
          class="footerTable"
          :showFirstLastPage="true"
          :pagination="props.pagination"
          :options.sync="options"
          :items-per-page-options="itemsPerPageOptions"
        ></v-data-footer>
      </template>
    </v-data-table>

    <div v-if="attributesTable.dataTable.externalPagination" class="text-center pt-2">
      <v-pagination v-model="options.page" :length="pageCount"></v-pagination>
    </div>
  </div>
</template>

<script>
import Swal from 'sweetalert2';
import * as HANDLERS from '@/helpers/types/handlers';
import TemplateHome from '../../views/dev/TemplateHome.vue';
import dataTableMixin from '@/mixins/data-table-mixin';
import { COMPONENT_TYPE } from '@/enums/types';
import * as validations from '@/helpers/validations';

export default {

  mixins: [dataTableMixin],

  components: {
    TemplateHome,
    i4proForm: () => import('@/components/layouts/i4pro-form'),
    i4proList: () => import('@/components/layouts/i4pro-list'),
    i4proSubPages: () => import('@/components/layouts/i4pro-sub-pages'),
    i4proRecordActionItem: () => import('@/components/partials/i4pro-record-action-item'),
  },

  props: {
    page: { type: String },
    items: { type: Array, default: () => [] },
    schema: { Type: Object, default: () => ({}) },
    headers: { Type: Object, default: () => ({}) },
    initialItems: { type: Array, default: () => [] },
    lookupwindow: { type: Boolean, default: false }
  },

  data: () => ({
    enabled: true,
    slots: ['footer'],
    slotAction: [],
    selected: [],
    expanded: [],
    search: '',
    loading: false,
    lastPage: 0,
    pageCount: 0,
    currentItems: [],
    initTable: true,

    renderExpandDetails: false,
    expandDetailsComponent: '',
    expandDetailsPage: 0,
    expandDetailsContext: '',
  }),

  events: {
    [HANDLERS.VALIDATE]: 'validate',
    [HANDLERS.LOAD_DATA_VALUES_PAGINATE]: 'fetchItems'
  },

  created() {
    this.attributesTable.dataTable.expandAll && (this.expanded = this.initialItems);
  },

  mounted() {
    this.options.itemsPerPage = this.setItemsPerPage()
    
    if (this.keyTargetSelect) {
      this.selected = this.items.filter( i => i[this.keyTargetSelect] );
    }

    this.initialOptions = structuredClone(this.options)
  },

  computed: {
    headerComputed() {
      if (!!this.expandHeaders.length) {
        const expandRecordActionsLength = this.schema.recordActionsModel.filter(r => r.expand).length

        // se todas as recordActions forem expand, remover o header de value recordAction
        if (!!expandRecordActionsLength && expandRecordActionsLength === this.schema.recordActionsModel.length) {
          return structuredClone(this.headers).filter(h =>!h.expand && h.value !== "recordAction")
        }
        
        return structuredClone(this.headers).filter(h => !h.expand)
      }

      return structuredClone(this.headers).filter(h => h.value !== "data-table-expand" && h.value !== "data-table-select")
    },

    attributesTable() {
      return this.lookupwindow ? this.schema : this.schema.attributes.attributesTable;
    },

    showTableFooter(){
      return (this.attributesTable.dataTable || {}).itemsPerPage !== -1
    },

    actions() {
      return this.schema.recordActions || {};
    },

    schemaFields() {
      return this.schema.lookups;
    },

    isEditable() {
      return 'fields' in this.schema;
    },

    hasInlineConditions() {
      return this.schema.inlineConditions.length > 0 ? true : false;
    },

    itemsPerPageOptions() {
      return 'itemsPerPageOptions' in this.attributesTable
        ? this.attributesTable.itemsPerPageOptions
        : [
            {
              text: '5',
              value: 5
            },
            {
              text: '10',
              value: 10
            },
            {
              text: '20',
              value: 20
            },
            {
              text: '50',
              value: 50
            },
            {
              text: '100',
              value: 100
            },
            {
              text: 'Todos',
              value: -1
            }
          ];
    },

    keyTargetSelect() {
      return this.attributesTable?.keyTargetSelect && this.attributesTable?.keyTargetSelect
    },

    schemaExpandDetails() {
      return this.schema ? this.schema.expandDetails : false
    },

    expandHeaders() {
      const expandHeaders = this.headers.filter(h => h.expand)
      const hasExpandRecordAction = !!this.schema?.recordActionsModel?.filter(r => r.expand).length || false

      if (hasExpandRecordAction) {
        const recordActionHeader = this.headers.find(h => h.value === "recordAction")
        expandHeaders.unshift(recordActionHeader)
      }

      return expandHeaders
    },

    // TODO: propriedade computada mas nunca usada. Remover definitivamente se nao houver efeito colateral
    // itensLength() {
    //   return this.items.length;
    // }
  },

  methods: {

    getStyle(value, key, item){
      let styleField = key + "_style";
      if(item[styleField] !== undefined){
        return item[styleField];
      }

      return null;      
    },

    isFlat(flatTable) {
      if (flatTable)
        return 'box-shadow:none!important; margin-left: 0px; margin-right: 0px; border-bottom: 1.5px solid #e6e6e6; padding-bottom: 5px;';
      else return null;
    },

    validate({ page, mode }) {
      // Em casos de multipages, onde a ação master é disparada, será necessário validatar a page irmã.
      let hasActionShared = this.$store.getters['page/hasActionShared'](page, mode);
      if ((page !== this.page || mode !== 'list') && !hasActionShared) return;

      let valid = true;
      for (let [key, value] of Object.entries(this.$refs)) {
        if (key !== 'refreshTable' && this.$refs[key]) {
          if (!this.$refs[key].validate(true)) {
            this.showMessageRequired(false);
            valid = false;
          }
        }
      }

      this.$emit('valid', valid);
    },

    showMessageRequired(valid) {
      if (!valid) {
        this.$store.dispatch('notification/showNotification', {
          color: 'error',
          text: 'Existem campos obrigatórios não preenchidos',
          showIconNotification: false
        });
      }
    },

    getFilteredActions(item, filterFn) {
      return Object.keys(this.actions).length && this.actions[item[this.attributesTable.keyValue]]
        ? this.actions[item[this.attributesTable.keyValue]].filter(filterFn)
        : [];
    },

    lineActions(item) {
      return this.getFilteredActions(item, x => x.visible && !x.col && !x.expand);
    },

    expandLineActions(item) {
      return this.getFilteredActions(item, x => x.visible && !x.col && x.expand);
    },

    getCols() {
      if (this.items.length && Object.keys(this.actions).length) {
        let cols = this.actions[this.items[0][this.attributesTable.keyValue]].reduce((acc, item) => {
          if (item?.col) {
            acc.push(item?.col)
          }

          return acc
        }, [])

        this.slotAction = cols
      }
    },

    lineActionsSlot(item, slot) {
      return Object.keys(this.actions).length && this.actions[item[this.attributesTable.keyValue]]
        ? this.actions[item[this.attributesTable.keyValue]].filter(x => x.visible && x?.col == slot)
        : [];
    },

    edit(item) {
      this.$emit('edit-item', { item, mode: this.attributesTable.editMode, origin: 'list' });
    },

    getItemModify(item) {
      return this.initialItems.find(x => x[this.attributesTable.keyValue] === item[this.attributesTable.keyValue]);
    },

    groupActions() {
      let action = false;
      let recordAction = false;
      for (var i = 0; i < this.headers.length; i++) {
        if (this.headers[i].value === 'action') action = true;
        else if (this.headers[i].value === 'recordAction') recordAction = true;
      }

      if (action && recordAction) return false;
      else return true;
    },

    conditioningActions(i) {
      if (this.lookupwindow || !this.schema.recordActionsModel.length) return;
      const key = i[this.schema.attributes.attributesTable.keyValue];
      const actions = this.$store.getters['page/actions']({ page: this.page, key });

      if (actions) {
        return actions.filter(x => x.visible);
      } else {
        return;
      }
    },

    getIndex(item) {
      let index;
      const key = this.schema.attributes.attributesTable.keyValue;

      this.items.forEach((field, i) => {
        if (field[key] == item[key]) {
          index = i;
        }
      });

      return index;
    },

    getFieldIdPrefix(item){
      return this.page + '[' + this.getIndex(item) + ']'
    },

    schemaFieldsSelected(item) {
      return this.schemaFields[item[this.attributesTable.keyValue]];
    },

    selectItem({ item, value }) {
      if ( this.keyTargetSelect && this.attributesTable.dataTable?.preserveModel ) {
        this.items.forEach( i => {
          if ( i[this.attributesTable.keyValue] === item[this.attributesTable.keyValue] ) {
            i[this.keyTargetSelect] = value;
          }
        });

        this.$emit('update-model', this.items);
      }
      this.$emit('select-item', item);
      if (this.lookupwindow) this.selected = [];
    },

    selectAllItem ({ items, value }) {
      if ( this.keyTargetSelect && this.attributesTable.dataTable?.preserveModel ) {
        this.items.forEach( i =>  i[this.keyTargetSelect] = value );

        this.$emit('update-model', this.items);
      }
      this.$emit('select-all-item', items);
      if (this.lookupwindow) this.selected = [];
    },

    handleCloseExpandedItem(item) {
      const isValid = this.validateExpandedEditItem(item)
      if (!isValid) return

      this.expanded = this.expanded.filter(
        el => el[this.attributesTable.keyValue] !== item[this.attributesTable.keyValue]
      );

      this.$emit('close-expanded');
    },

    expandItems(item) {
      const tableKey = this.attributesTable.keyValue
      const foundItem = this.items.find(c => c[tableKey] === item[tableKey])
      return [foundItem]
    },

    validateExpandedEditItem(item) {
      let index = item[this.attributesTable.keyValue]
      if (index) {
        const fields = this.schemaFields[index]

        let fieldsWithRules = []
        if (fields && fields.length) fieldsWithRules = fields.filter(field => field.rules && field.rules.length)
        if (fieldsWithRules.length) {
          for (const field of fieldsWithRules) {
            const modelKey = field.model
            const modifiedItem = this.getItemModify(item)
            const modelValue = modifiedItem[modelKey]

            for (const rule of field.rules) {
              const validationFn = validations[rule]
              if (validationFn) {
                if (validationFn(modelValue) !== true) {
                  this.$store.dispatch('notification/showNotification', {
                    color: 'error',
                    text: 'Por favor, verifique os campos e tente novamente',
                    showIconNotification: false
                  });
  
                  return false
                }
              } else {
                console.error(`Regra de validação ${rule} não encontrada`)
              }
            }
          }
        }
      };

      return true
    },

    handleExecuteAction(event) {
      const { action, origin, model } = event
      this.executeAction(action, origin, model)
    },

    executeAction(action, origin, model) {
      if (action.confirmMessage) {
        this.confirmDialog(action, origin, model);
      } else {
        this.action(action, origin, model);
      }
    },

    async action(action, origin, model) {
      this.$store.dispatch('page/executeAction', {
        page: this.page,
        mode: 'list',
        origin,
        action,
        model,
        index: this.getIndex(model)
      });
    },

    confirmDialog(action, origin, model) {
      const {
        title,
        text,
        html,
        icon,
        showCancelButton,
        confirmButtonText,
        cancelButtonText,
        reverseButtons,
        confirmButtonClass,
        cancelButtonClass
      } = action.confirmMessage;

      let defaultClassConfirm = confirmButtonClass || 'primary';
      let defaultClassCancel = cancelButtonClass || 'error';

      const swalWithBootstrapButtons = Swal.mixin({
        customClass: {
          confirmButton: `v-btn v-size--default ${defaultClassConfirm}`,
          cancelButton: `v-btn v-size--default error ${defaultClassCancel}`
        },
        buttonsStyling: false
      });

      swalWithBootstrapButtons
        .fire({
          title,
          text,
          html,
          icon,
          showCancelButton,
          confirmButtonText,
          cancelButtonText,
          reverseButtons
        })
        .then(result => {
          if (result.value) this.action(action, origin, model);
        });
    },

    compareValue(items, old) {
      // Validações para melhorar performance.
      //  As dispatch abaixo eram executadas várias vezes por não analisar diferença profunda entre items e old
      let interrupt = false;
      if (old?.length === items?.length) {
        interrupt = items?.every((obj, index) => {
          return Object.keys(obj).every(keyTarget => obj[keyTarget] === old[index][keyTarget]);
        });
      }

      return interrupt
    },

    async loadExpandDetails({item, value}) {
      if (!value || !this.schemaExpandDetails) {
        return
      }

      this.expandDetailsComponent = COMPONENT_TYPE[this.schemaExpandDetails.mode];
      this.expandDetailsPage = this.schemaExpandDetails.page
      this.expandDetailsContext = this.schemaExpandDetails.mode

      await this.$store.dispatch('page/load', {
        page: this.expandDetailsPage,
        mode: this.expandDetailsContext,
        parent: this.page,
        redirectParams: { ...item }
      });

      this.renderExpandDetails = true
    },
  },

  watch: {
    // TODO: propriedade nunca usada em nenhuma parte. Remover definitivamente se não houver efeito colateral
    // itensLength(newValue) {
    //   this.attributesTable.dataTable.expandAll && (this.expanded = this.items);
    // },

    selected(newValue) {
      if (this.lookupwindow) return;

      let keyValue = this.schema.attributes.attributesTable.keyValue;
      let actionConditionsOnSelectedItems = (this.schema?.conditionsOnSelectedItems?.actionsToReadOnly) ?? [];

      const selectedItems = this.initialItems.filter(item =>
        newValue.find(x => {
          if (x[keyValue] === item[keyValue]) return item;
        })
      );
      this.$store.dispatch('page/updateSelectedItems', {
        selectedItems,
        keyValue,
        conditions: {
          actionConditionsOnSelectedItems,
          page: this.page,
          mode: 'list'
        }
      });
    },

    async currentItems(items, old) {
      // TODO: Interrupção é necessária para melhor performance. Investigar futuramente pq a limpeza do VUEX (page/clean) não executa a setLookupListScheme
      // let interrupt = this.compareValue(items, old);
      // if (interrupt || !items.length || this.lookupwindow) return;

      if (!items.length || this.lookupwindow) return;

      if (this.keyTargetSelect) {
        this.selected = this.items.filter( i => i[this.keyTargetSelect] );
      }

      if (this.isEditable) {
        await this.$store.dispatch('page/setLookupListScheme', { items, page: this.page });
        this.attributesTable.dataTable.expandAll && (this.expanded = items);
      }
      
      await this.$store.dispatch('page/setActionsCondition', this.page);
      await this.$store.dispatch('page/initInlineConditionsList', { page: this.page, mode: 'list', items });
      await this.$store.dispatch('page/initConditionsList', {
        page: this.page,
        mode: 'list',
        items,
        key: this.schema.attributes.attributesTable.keyValue
      });
      await this.$store.dispatch('page/initConditionsOnSelectedItems',{
        page: this.page,
        mode: 'list',
        key: this.schema.attributes.attributesTable.keyValue,
        tab: this.formOptions?.tab,
        step: this.formOptions?.step
      });

      const pageFilter = this.$store.getters['page/pageFilter'](this.page);
      
      if(pageFilter?.offset === 0) {
        this.lastPage = 0;
        this.options.page = 1
      }

      this.lastPage = this.options.page;
      this.getCols()
    },

    items() {
      if ( this.keyTargetSelect && this.attributesTable.dataTable?.preserveModel ) return;
      this.selected = [];
    },
  }
};
</script>

<style lang="scss" scoped>
@media (max-width: 598px) {
  .v-data-table__wrapper {
    table {
      tr {
        display: flex;
      }
    }
  }
}
.action-col {
  margin: auto auto;
  button {
    margin-left: 10px;
  }
}
</style>
